/**
 * Role — the functional primitive above the agent (NEX-2514).
 *
 * A Role is the container a company hires, and this list is what it owns
 * EXCLUSIVELY — `RoleResource @@unique([organizationId, resourceType,
 * resourceId])` means at most one Role per resource per org, org-wide.
 * It is deliberately its OWN list, not `ResourceType` from the permission
 * system: a Role holds operational resources (the things a "super human" works
 * with), which is a different set from what ReBAC grants are written against.
 *
 * 🔴 EXCLUSIVITY IS THE MEMBERSHIP TEST, NOT "IS IT A SYSTEM A ROLE USES".
 * Three kinds a Role plainly uses are NOT here, and all three are absent for
 * one reason: several Roles legitimately hold the same one, which that unique
 * key cannot express. Each lives in its own M:N grant table instead —
 * `RoleCollectionGrant`, `RoleWorkspaceGrant`, `RoleExternalToolGrant`, each
 * keyed `@@unique([roleId, <target>Id])`.
 *
 * So the question to ask of a NEW kind is not whether a Role uses it. It is:
 * can two Roles in one organisation hold this same row at once? Yes ⇒ a grant
 * table. No ⇒ here.
 *
 * Adding a member kind here: add it, then teach the clone/move flows what it
 * means to reuse that resource across Roles (an agent clones cleanly; a
 * deployment often cannot — see the mission's per-resource clone note).
 */
import { z } from "zod";

export const RoleResourceType = {
  AGENT: "agent",
  WORKFLOW: "workflow",
  DEPLOYMENT: "deployment",
  AI_TASK: "ai_task",
  DOCUMENT_TEMPLATE: "document_template"
} as const;

export type RoleResourceType = (typeof RoleResourceType)[keyof typeof RoleResourceType];

export const ROLE_RESOURCE_TYPES = Object.values(RoleResourceType) as [
  RoleResourceType,
  ...RoleResourceType[]
];

export const RoleResourceTypeSchema = z.enum(ROLE_RESOURCE_TYPES);

/**
 * Runtime membership check for a stored `RoleResource.resourceType`.
 *
 * That column is loose TEXT, not a database enum, so it can hold a value this
 * build does not know — including a member that was RETIRED. `collection` is the
 * worked example: it was a `RoleResourceType` until its rows moved to
 * `RoleCollectionGrant`, and the old rows did not disappear when the union
 * narrowed.
 *
 * `external_tool` was retired the same way on 2026-08-13, into
 * `RoleExternalToolGrant`, and its retirement is the CLEAN case rather than a
 * second instance of the hazard: it was measured at ZERO rows on production and
 * on staging before the union narrowed, because the only write path
 * (`AssignResourceUseCase` → `FindExternalToolByIdPort`) was strict-org while
 * every tool worth holding is in the shared catalogue, so no row was ever
 * written. It is still refused here, because a loose TEXT column is no place to
 * assume a measurement stays true.
 *
 * ⚠️ THIS IS NOT `isRoleBoardCardType`, AND THE TWO ARE ONE IDENTIFIER APART.
 * `ROLE_BOARD_CARD_TYPES` is this set PLUS `collection`, `workspace` and
 * `external_tool`, which are legitimate CARD kinds sourced from the three grant
 * tables — but never legitimate `RoleResource` rows. Narrowing a `RoleResource`
 * row with the card guard
 * therefore ACCEPTS exactly the retired rows that must be dropped, and the
 * resulting card renders and then fails every move, because the move path writes
 * the grant table where no matching row exists. Use this guard for a row read
 * out of `RoleResource`; use the card guard only at the API boundary.
 */
export const isRoleResourceType = (value: unknown): value is RoleResourceType =>
  typeof value === "string" && (ROLE_RESOURCE_TYPES as readonly string[]).includes(value);

/**
 * The subset of `RoleResourceType` that can ever be the agent-runtime CALLER of
 * a Role check — mirrors the backend's `RoleCallerResource`
 * (`apps/backend/src/roles/domain/value-objects/role-caller-resource.ts`),
 * whose own docblock states the reason: only an Agent or a Workflow executes.
 * A Deployment, an AI task or a Document template is a thing a Role is
 * granted, never a thing that calls — so a route asking "is THIS resource
 * currently confined for retrieval" has no answer for the other three, and
 * this schema refuses the question at the boundary rather than accepting an
 * id the backend could never resolve a caller for.
 */
export const RoleRuntimeCallerResourceTypeSchema = z.enum([
  RoleResourceType.AGENT,
  RoleResourceType.WORKFLOW
]);

export type RoleRuntimeCallerResourceType = z.infer<typeof RoleRuntimeCallerResourceTypeSchema>;
